import {
  ITEM_TYPE_LABELS,
  ITEM_TYPE_TO_BACKEND,
  type ItemType,
  type PricebookListParams,
} from '@/types/pricebook';

/**
 * `archived` sits beside the item types because it is the only other slice
 * of the catalog a user ever asks for: archived items are hidden from every
 * default list, and Restore lives on the item, so without a way to list them
 * archiving was effectively one-way.
 */
export type PricebookTypeFilter = ItemType | 'all' | 'archived';

/**
 * The server needs `company:edit` or `pricing:edit` (the keys that can restore
 * an item) to lift its soft-delete filter, so a user with neither must not be
 * offered the pill at all — they would only get a 403.
 */
export const PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS = ['company:edit', 'pricing:edit'] as const;

export interface PricebookTypeFilterOption {
  value: PricebookTypeFilter;
  label: string;
}

const TYPE_OPTIONS: PricebookTypeFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'service', label: ITEM_TYPE_LABELS.service },
  { value: 'material', label: ITEM_TYPE_LABELS.material },
  { value: 'equipment', label: ITEM_TYPE_LABELS.equipment },
  { value: 'membership', label: ITEM_TYPE_LABELS.membership },
  { value: 'fee', label: ITEM_TYPE_LABELS.fee },
];

const ARCHIVED_OPTION: PricebookTypeFilterOption = { value: 'archived', label: 'Archived' };

/** Every value the filter can legally hold — used to validate URL search params. */
export const PRICEBOOK_TYPE_FILTER_VALUES: readonly PricebookTypeFilter[] = [
  ...TYPE_OPTIONS.map((o) => o.value),
  ARCHIVED_OPTION.value,
];

/** The pills to render. `archived` is offered only when the user may list it. */
export function pricebookTypeFilterOptions(canSeeArchived: boolean): PricebookTypeFilterOption[] {
  return canSeeArchived ? [...TYPE_OPTIONS, ARCHIVED_OPTION] : TYPE_OPTIONS;
}

/**
 * The `usePricebookItems` params one filter value stands for. Archived rows
 * are soft-deleted on the server, so `status=ARCHIVED` alone matches nothing:
 * the default `isDeleted=false` clause has to be lifted alongside it.
 */
export function pricebookListParamsForTypeFilter(
  filter: PricebookTypeFilter | '',
): Pick<PricebookListParams, 'type' | 'status' | 'includeArchived'> {
  if (filter === '' || filter === 'all') return {};
  if (filter === 'archived') return { status: 'ARCHIVED', includeArchived: true };
  return { type: filter };
}

/** The same slice as raw query-string params, for callers that bypass the hook (export). */
export function pricebookWireParamsForTypeFilter(
  filter: PricebookTypeFilter | '',
): Record<string, string> {
  const params = pricebookListParamsForTypeFilter(filter);
  const wire: Record<string, string> = {};
  if (params.type) wire.type = ITEM_TYPE_TO_BACKEND[params.type] ?? params.type.toUpperCase();
  if (params.status) wire.status = params.status;
  if (params.includeArchived) wire.includeArchived = 'true';
  return wire;
}
